import { ApiError } from '@central/api';

/** Text for server codes both apps meet; an app's overrides win. */
const shared: Record<string, string> = {
  invalid_credentials:
    'Email, password or passkey not accepted. Five failed attempts lock the account for 15 minutes.',
  invalid_code: 'That code was not accepted. Codes change every 30 seconds; each works once.',
  reauth_required: 'Confirm it’s you with your second factor, then try again.',
  no_second_factor: 'Set up a passkey or an authenticator app before creating recovery codes.',
  already_enrolled: 'An authenticator app is already set up for this account.',
  no_enrollment: 'That setup step expired. Start it again.',
  no_passkey: 'This account has no passkey. Use your authenticator app.',
  rate_limited: 'Too many sign-in attempts from this network. Wait a minute and try again.',
  csrf: 'Your session expired. Reload the page and sign in again.',
  bad_origin: 'This request came from an unexpected origin.',
  license_suspended: 'This license is suspended.',
  license_expired: 'This license has expired.',
  offline_file_not_found: 'No machine holds an offline activation on this license.',
};

/**
 * User-facing text for a failed call: the server's code (the app's own text,
 * then the shared text), then the HTTP status, then a WebAuthn failure
 * (DOMException), a missing second factor (SecondFactorNeeded, whose text is
 * already for the user), and anything else as a network failure.
 */
export function errorMessage(err: unknown, messages: Record<string, string> = {}): string {
  if (err instanceof Error && err.name === 'SecondFactorNeeded') return err.message;
  if (err instanceof ApiError) {
    const byCode = messages[err.code] ?? shared[err.code];
    if (byCode) return byCode;
    if (err.status === 403) return 'Your role cannot do this.';
    if (err.status === 404) return 'Not found. It may have been removed.';
    if (err.status >= 500) return 'Central had a problem. Try again in a moment.';
    return err.message || 'The request was not accepted.';
  }
  if (err instanceof DOMException)
    return err.name === 'NotAllowedError'
      ? 'The passkey request was cancelled or timed out.'
      : 'The passkey request failed. Try again.';
  return 'Could not reach Central. Check your connection and try again.';
}

export function isApiError(err: unknown, code: string): err is ApiError {
  return err instanceof ApiError && err.code === code;
}
